import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useApolloClient } from '@apollo/client';

import Item from './Components/Item';
import { LocalItem } from '~Types';
import { useBasketCalculator } from '~Scenes/CreateOrder/Components/Basket';
import { useCurrency, useI18n, useSelector } from '~Hooks';
import OfferItemWrapper from './Components/OfferItemWrapper';
import TouchedWrapper from '~Scenes/SelfServiceKiosks/Components/TouchedWrapper';
import { checkCartItemsAvailability } from '../../utils/cart';
import { useInventoryUniverse } from '~Components/InventoryUniverse';
import OutOfStockAlert from './Components/OutOfStockAlert';
import { companiesWithTakeAwayBag } from '~Scenes/SelfServiceKiosks';
import TakeAwayBagAlert from '~Scenes/SelfServiceKiosks/Scenes/Cart/Components/takeAwayBagAlert';
import { groupOfferMatches } from '~Utils/offers';
import { useItemsPackager } from '~Utils/packagedItems';

const Wrapper = styled.div`
  overflow-y: auto;
  height: 100%;
`;

const Inner = styled.div`
  padding: 56px 164px 356px 164px;
  position: relative;

  @media (orientation: landscape) {
    padding: 40px 60px 280px 60px;
  }
`;

const Title = styled.h2`
  margin: 0 0 24px 0;
  color: rgb(19, 19, 22);
  font-family: Eina01-SemiBold;
  font-size: 64px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 72px;
`;

const Description = styled.p`
  margin: 0;
  color: rgb(23, 23, 26);
  font-family: Eina01-Regular;
  font-size: 16px;
  font-weight: normal;
  letter-spacing: 0.24px;
  line-height: 22px;
`;

const Items = styled.div`
  padding: 31px 0 0 0;
`;

const Button = styled(TouchedWrapper)`
  height: 200px;
  width: 752px;
  box-sizing: border-box;
  padding: 0 48px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgb(19, 19, 22);
  border-radius: 16px;

  @media (orientation: landscape) {
    height: 150px;
    width: 752px;
  }
`;

const ButtonLabel = styled.div`
  color: rgb(255, 255, 255);
  font-family: Eina01-SemiBold;
  font-size: 48px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 56px;

  @media (orientation: landscape) {
    font-size: 36px;
    line-height: 42px;
  }
`;

const ButtonAmount = styled.div`
  color: rgb(255, 255, 255);
  font-family: Eina01-Regular;
  font-size: 48px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 56px;
  text-align: right;

  @media (orientation: landscape) {
    font-size: 36px;
    line-height: 42px;
  }
`;

const BottomButton = styled.div`
  position: fixed;
  bottom: 104px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;

  @media (orientation: landscape) {
    bottom: 40px;
  }
`;

interface Props {
  orderId: string | null;
  venueId: string;
  items: LocalItem[];
  setItems: React.Dispatch<React.SetStateAction<LocalItem[]>>;
  checkLegalAgeAtCheckout: (leagalCheck: boolean) => void;
  onClose: () => void;
  onClickItem: (item: LocalItem) => void;
}

const Cart = ({
  orderId,
  venueId,
  items,
  setItems,
  onClose,
  checkLegalAgeAtCheckout,
  onClickItem,
}: Props) => {
  const inventory = useInventoryUniverse();
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [outOfStockItems, setOutOfStockItems] = useState<LocalItem[]>([]);
  const [showTakeAwayBagAlert, setShowTakeAwayBagAlert] = useState(false);
  const { i18n } = useI18n();
  const calculator = useBasketCalculator(venueId);
  const currency = useCurrency();
  const client = useApolloClient();
  const order = useSelector((state) => state.POSOrders.find((o) => o.id === orderId));
  const companyId = useSelector((state) => state.company);

  const packaged = useItemsPackager(
    items,
    venueId,
    order?.discountRate ?? null,
    order?.discountType ?? null,
    true,
  );

  const [total] = calculator(packaged);

  const takeAwayBagId = useMemo(() => {
    return companiesWithTakeAwayBag.find((c) => c.companyId === companyId)?.itemId;
  }, [companyId]);

  const grouped = useMemo(() => {
    const result = groupOfferMatches(items, venueId, client, i18n);

    // If there is a takeaway bag, move to the end of the list
    const takeAwayBagItem = result.unmatched.find((u) => u.itemId === takeAwayBagId);
    if (takeAwayBagItem) {
      const itemsWithoutBag = result.unmatched.filter((u) => u.itemId !== takeAwayBagId);
      result.unmatched = [...itemsWithoutBag, takeAwayBagItem];
    }
    return result;
  }, [items, venueId, client, takeAwayBagId]);

  const deleteItemFromOrder = (id: string) => {
    setItems((_items) =>
      _items.filter((item) => {
        // Delete the item we want to delete
        if (item.id === id) {
          return false;
        }

        // Delete any upsell that was bought with this item
        if (item.parentId === id) {
          return false;
        }

        return true;
      }),
    );
  };

  const handlePay = () => {
    const { hasOutOfStock, availableItems, unavailableItems } = checkCartItemsAvailability(
      items,
      inventory.items,
    );

    if (hasOutOfStock) {
      setOutOfStockItems(unavailableItems);
      setItems(availableItems);
      return;
    }

    onClose();
    checkLegalAgeAtCheckout(packaged.some((item) => item.ageRestricted));
  };

  const handleSelectItem = (id: string) => {
    if (id !== selectedItemId) {
      setSelectedItemId(id);
    } else {
      setSelectedItemId(null);
    }
  };

  const onShowTakeAwayBagAlert = (value: boolean) => {
    setShowTakeAwayBagAlert(value);
  };

  useEffect(() => {
    if (items.length === 0 && outOfStockItems.length === 0) {
      onClose();
    }
  }, [items, outOfStockItems]);

  useEffect(() => {
    // If there is only a takeaway bag in the cart, empty the cart
    if (items.length === 1) {
      const takeAwayBagItem = items.find((i) => i.itemId === takeAwayBagId);
      if (takeAwayBagItem) {
        setItems([]);
      }
    }
  }, [items]);

  return (
    <Wrapper>
      <Inner>
        <Title>{i18n('Express.Cart.Title.Expanded')}</Title>
        <Description>{i18n('SSK.Cart.Description')}</Description>
        <Items>
          {grouped.matches.map((offerMatch, index) => (
            <OfferItemWrapper key={index} offerId={offerMatch.offerId}>
              {offerMatch.items
                .map(({ id }) => packaged.find((i) => i.id === id)!)
                .map((item) => (
                  <Item
                    key={item.id}
                    item={item}
                    selected={item.id === selectedItemId}
                    isOffer={true}
                    onSelectItem={handleSelectItem}
                    onDeleteItemFromOrder={deleteItemFromOrder}
                    entry={packaged.find((i) => i.id === item.id)!}
                    onClickItem={onClickItem}
                  />
                ))}
            </OfferItemWrapper>
          ))}
          {grouped.unmatched.map((item) => (
            <Item
              key={item.id}
              item={item}
              selected={item.id === selectedItemId}
              isOffer={false}
              onSelectItem={handleSelectItem}
              onDeleteItemFromOrder={deleteItemFromOrder}
              takeAwayBagId={takeAwayBagId}
              entry={packaged.find((i) => i.id === item.id)!}
              onClickItem={onClickItem}
              onShowTakeAwayBagAlert={onShowTakeAwayBagAlert}
            />
          ))}
        </Items>
        <BottomButton>
          <Button
            onClick={handlePay}
            shadows={{
              pressed: '0px 23px 36px 3px rgba(0, 0, 0, 0.24)',
              init:
                '0px 11px 15px -7px rgba(0, 0, 0, 0.32), 0px 9px 46px 8px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.24)',
            }}>
            <ButtonLabel>{i18n('Express.Cart.Pay')}</ButtonLabel>
            <ButtonAmount>{currency.format(total)}</ButtonAmount>
          </Button>
        </BottomButton>
        <OutOfStockAlert
          items={outOfStockItems}
          visible={Boolean(outOfStockItems.length)}
          onClose={() => setOutOfStockItems([])}
        />
        <TakeAwayBagAlert
          visible={showTakeAwayBagAlert}
          onClose={() => onShowTakeAwayBagAlert(false)}
        />
      </Inner>
    </Wrapper>
  );
};

export default Cart;
